import React, { memo } from 'react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { NewSuggestion, ProductSuggestion, SlicedSuggestions } from 'types/suggestions';
import { useLang } from 'common/hooks';
import { getDomain } from 'utils';
import { useSuggestionsButtonAction } from '../hooks/use-suggestions-button-action';
import { NotFoundSuggestion } from './not-found-suggestion';
import { SuggestionsDefaultListPinnedItems } from './suggestions-list/suggestions-default-list-pinned-items';
import { useSiteDomain } from '../hooks/use-site-domain';
import { useSponsoredProducts } from 'components/header/hooks';
import { ProductsList } from './products-list';
import { CategoriesList } from './categories-list';
import { ServicesList } from './services-list';
import { SponsoredProductsList } from './sponsored-products-list';
import { getServicesListDivider } from './utils/get-with-divider';
import { useSponsoredBrands } from '../hooks';
import { NewSuggestionsList } from './new-suggestions-list';
import { PinnedSuggestion } from 'types/model';

export type SuggestionsProps = {
  searchQuery: string;
  suggestions: SlicedSuggestions | null;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: () => void;
  onSelectSuggestion?: () => void;
  searchHref: string;
  suggestionSelector: string;
  index: number;
  isSrp: boolean;
  notFoundMessage: string | false | undefined;
  collapse: () => void;
  newSuggestions: boolean;
  pinnedSuggestions: PinnedSuggestion[];
};

export const Suggestions = memo<SuggestionsProps>(
  ({
    searchQuery,
    suggestions,
    suggestionSelector,
    onMouseEnter,
    onMouseLeave,
    searchHref,
    index,
    isSrp,
    onSelectSuggestion,
    notFoundMessage,
    collapse,
    newSuggestions,
    pinnedSuggestions,
  }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [slicedProducts, slicedServices, slicedPages, slicedCategories] = suggestions ?? [[], [], [], []];
    const sponsoredProducts = useSponsoredProducts();
    const { itemsToDisplay: sponsoredBrands } = useSponsoredBrands();
    const { host } = useStoreon<State>('host');
    const domain = getDomain(host);
    const { isKzDomain } = useSiteDomain(domain);
    const { lang } = useLang();
    const showServices = !(isKzDomain || lang === 'kk');
    const showProducts = !(isKzDomain && lang === 'kk');
    const isDefaultList = searchQuery === '';
    const noSuggestions =
      slicedProducts.length === 0 && slicedServices.length === 0 && slicedCategories.length === 0;
    const hideSuggestions = !showProducts && !showServices && !isDefaultList;
    const showOnlyCategories = isDefaultList && slicedCategories.length > 0;

    const buttonAction = useSuggestionsButtonAction({
      onMouseEnter,
      onMouseLeave,
      onSelectSuggestion,
      searchQuery,
      isPersonalization: showOnlyCategories,
    });

    if (!suggestions) return null;

    const renderSponsoredProducts = () => (
      <SponsoredProductsList
        suggestions={suggestions}
        index={index}
        isSrp={isSrp}
        isDefaultList={isDefaultList}
        suggestionSelector={suggestionSelector}
        sponsoredProducts={sponsoredProducts}
        searchQuery={searchQuery}
        withDivider={sponsoredBrands.length > 0 || sponsoredProducts.length > 0}
        isKzDomain={isKzDomain}
        buttonAction={buttonAction}
      />
    );

    if (noSuggestions || hideSuggestions) {
      if (notFoundMessage === false) {
        return null;
      }
      return (
        <>
          <NotFoundSuggestion
            notFoundMessage={notFoundMessage}
            searchHref={searchHref}
            searchQuery={searchQuery}
            collapse={collapse}
          />

          {renderSponsoredProducts()}
        </>
      );
    }

    return (
      <>
        {isDefaultList && !isKzDomain ? (
          <SuggestionsDefaultListPinnedItems
            buttonAction={buttonAction}
            index={index}
            isSrp={isSrp}
            suggestionSelector={suggestionSelector}
            pinnedSuggestions={pinnedSuggestions}
          />
        ) : null}

        {newSuggestions && (
          <NewSuggestionsList
            showProducts={showProducts}
            slicedNewSuggestions={slicedProducts as NewSuggestion[]}
            buttonAction={buttonAction}
            showOnlyCategories={showOnlyCategories}
            suggestionSelector={suggestionSelector}
            isDefaultList={isDefaultList}
            searchHref={searchHref}
            index={index}
            isSrp={isSrp}
          />
        )}

        {!newSuggestions && (
          <ProductsList
            showProducts={showProducts}
            suggestionSelector={suggestionSelector}
            isDefaultList={isDefaultList}
            searchHref={searchHref}
            index={index}
            isSrp={isSrp}
            slicedProducts={slicedProducts as ProductSuggestion[]}
            buttonAction={buttonAction}
            showOnlyCategories={showOnlyCategories}
          />
        )}

        <CategoriesList
          suggestionSelector={suggestionSelector}
          isDefaultList={isDefaultList}
          slicedCategories={slicedCategories}
          productsLength={slicedProducts.length}
          suggestions={suggestions}
          index={index}
          isSrp={isSrp}
          buttonAction={buttonAction}
          showOnlyCategories={showOnlyCategories}
        />

        <ServicesList
          showServices={showServices}
          suggestions={suggestions}
          index={index}
          isSrp={isSrp}
          isDefaultList={isDefaultList}
          withDivider={getServicesListDivider(
            slicedProducts.length,
            slicedCategories.length,
            slicedServices.length,
          )}
          slicedServices={slicedServices}
          suggestionSelector={suggestionSelector}
          buttonAction={buttonAction}
          showOnlyCategories={showOnlyCategories}
        />

        {renderSponsoredProducts()}
      </>
    );
  },
);
